import UserFeedbackOpener, {
  CloseReasonType,
  FeedbackTypes,
  FirstLevelAnswerElementType,
  ImageTagSelectable,
  OpenerEventTypes,
  QuestionJustifyContent,
  QuestionPageTheme,
  ShowOptions,
} from '@wix/user-feedback-opener';
import { contains, isEmpty, map } from 'ramda';
import { isFunction } from 'ramda-adjunct';
import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useAsync } from 'react-use';
import { DEFAULT_TOPICS } from '../../../constants/RecommendationTopic';
import { MainSource } from '../../../constants/Source';
import { getLocale } from '../../env/getLocale';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import { parseSiteMediaToken } from '../../siteMediaToken';
import { useAdapter } from '../useAdapter';
import { useAppStatus } from '../useAppStatus';

export type TOpenUserFeedbackForRecommendedTopics = (recommendedTopics: string[]) => void;

const addSelectionToTags = (tags: ImageTagSelectable[], recommendedCategories: string[]): ImageTagSelectable[] =>
  map(
    (tag) => ({
      ...tag,
      isSelected: contains(tag.id, recommendedCategories),
    }),
    tags,
  );

const emptyRecommendedTopicsList = [];

const UserFeedbackContext = createContext<{
  userFeedback: UserFeedbackOpener;
  recommendedTopics: { id: string; imageUrl: string; title: string }[];
  openUserFeedbackForRecommendedTopics: TOpenUserFeedbackForRecommendedTopics;
}>({
  userFeedback: null,
  recommendedTopics: emptyRecommendedTopicsList,
  openUserFeedbackForRecommendedTopics: () => {},
});

export const UserFeedbackProvider: FC = ({ children }) => {
  const { formatMessage } = useIntl();
  const { mmgrSettings } = useMMGRSettings();
  const { isAppWarmingUp } = useAppStatus();
  const { siteMediaToken, origin } = mmgrSettings;
  const preloadTimeoutId = useRef<number>(null);

  const userFeedback = useMemo(() => {
    const { metaSiteId } = parseSiteMediaToken(siteMediaToken);

    return new UserFeedbackOpener({
      siteToken: siteMediaToken,
      initiator: 'mmg6',
      locale: getLocale(),
      metasiteId: metaSiteId,
      origin,
    });
  }, [siteMediaToken, origin]);

  const homeAdapter = useAdapter(MainSource.Home);
  const recommendedTopicsLoadingState = useAsync(() => homeAdapter.topics.list({ locale: getLocale() }), []);

  const recommendedTopics =
    recommendedTopicsLoadingState.loading || recommendedTopicsLoadingState.error
      ? emptyRecommendedTopicsList
      : recommendedTopicsLoadingState.value.data;

  const openUserFeedbackForRecommendedTopics = useCallback(
    (recommendedCategories) => {
      const tags = addSelectionToTags(
        recommendedTopics,
        isEmpty(recommendedCategories) ? DEFAULT_TOPICS : recommendedCategories,
      );

      const question: ShowOptions = {
        questionId: 'mmgrTopicsSelection',
        triggerEvent: 'configureTopics',
        noDontShowAgain: true,
        disableExposure: true,
        feedbackModel: {
          feedbackType: FeedbackTypes.Question,
          question: {
            title: formatMessage({ id: 'media-home.recommendation-section.feedback.title' }),
            subTitle: formatMessage({ id: 'media-home.recommendation-section.feedback.sub-title' }),
            justifyContent: QuestionJustifyContent.Start,
          },
          theme: QuestionPageTheme.Light,
          answer: {
            firstLevelType: FirstLevelAnswerElementType.ImageTags,
            cancelButton: {
              label: formatMessage({ id: 'media-home.recommendation-section.feedback.cancel-button' }),
            },
            applyButton: {
              choice: 'apply',
              label: formatMessage({ id: 'media-home.recommendation-section.feedback.apply-button' }),
            },
            tags,
          },
        },
      };

      userFeedback.showQuestion(question);
    },
    [userFeedback, recommendedTopics],
  );

  useEffect(() => {
    if (!userFeedback || isAppWarmingUp || preloadTimeoutId.current) {
      return;
    }

    if (process.env.NODE_ENV === 'production') {
      preloadTimeoutId.current = window.setTimeout(() => {
        userFeedback.preload();
      }, 5000);
    }
  }, [userFeedback, isAppWarmingUp]);

  useEffect(() => {
    return () => {
      clearTimeout(preloadTimeoutId.current);

      preloadTimeoutId.current = null;
      userFeedback.destroy();
    };
  }, [userFeedback]);

  return (
    <UserFeedbackContext.Provider value={{ userFeedback, openUserFeedbackForRecommendedTopics, recommendedTopics }}>
      {children}
    </UserFeedbackContext.Provider>
  );
};

export const useUserFeedback = () => useContext(UserFeedbackContext);

export const useUserFeedbackOnClose = (onClose = (_data) => {}) => {
  const { userFeedback } = useUserFeedback();

  useEffect(() => {
    if (!userFeedback) {
      return;
    }

    const closeSubscription = userFeedback.addListener(OpenerEventTypes.Closed, (data) => {
      if (data.closeEventPayload.closeReason === CloseReasonType.FinishedFlow && isFunction(onClose)) {
        onClose(data);
      }
    });

    return () => closeSubscription.remove();
  }, [userFeedback, onClose]);
};
